import { forwardRef, useImperativeHandle, useRef } from "react";
import { Button } from "./ui/button";

export interface ComposerHandle {
  focus: () => void;
  getValue: () => string;
  setValue: (value: string) => void;
  setSending: (sending: boolean) => void;
}

interface ComposerProps {
  textareaId: string;
  buttonId: string;
  placeholder: string;
  supportingCopy?: string;
  buttonLabel: string;
  onSubmit: (value: string) => Promise<void> | void;
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  { textareaId, buttonId, placeholder, onSubmit },
  ref,
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    getValue: () => textareaRef.current?.value ?? "",
    setValue: (value: string) => {
      if (textareaRef.current) {
        textareaRef.current.value = value;
      }
    },
    setSending: (sending: boolean) => {
      if (buttonRef.current) {
        buttonRef.current.disabled = sending;
        buttonRef.current.style.opacity = sending ? "0.5" : "";
      }
    },
  }));

  const submit = async () => {
    const value = textareaRef.current?.value ?? "";
    if (!value.trim()) {
      return;
    }
    await onSubmit(value);
  };

  return (
    <div className="relative rounded-[14px] border border-line bg-white/[0.04]">
      <textarea
        id={textareaId}
        ref={textareaRef}
        placeholder={placeholder}
        rows={2}
        className="w-full bg-transparent border-0 outline-none text-foreground placeholder:text-foreground/30 px-4 pt-3 pb-10 resize-none"
        onKeyDown={async (event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            await submit();
          }
        }}
      />
      <Button
        id={buttonId}
        ref={buttonRef}
        variant="primary"
        size="sm"
        className="absolute right-3 bottom-3"
        onClick={submit}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      </Button>
    </div>
  );
});
